import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Avatar,
  Skeleton
} from '@mui/material';
import {
  ArrowBack,
  Chat,
  Message,
  Group,
  AccessTime,
  TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';

interface ChatData {
  id: string;
  user1Id: string;
  user2Id: string;
  user1Name: string;
  user2Name: string;
  user1Avatar?: string;
  user2Avatar?: string;
  lastMessageTime: Date;
  messageCount: number;
  isActive: boolean;
  createdAt: Date;
}

const ChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState<ChatData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchChats = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`https://api.sobcof.com/api/chats?page=${page + 1}&limit=${rowsPerPage}&status=${statusFilter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setChats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* Enhanced App Bar */}
      <AppBar 
        position="static" 
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Chat sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Sohbet Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Chat sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {chats.length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Toplam Sohbet
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white'
            }}>
              <TrendingUp sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {chats.filter(c => c.isActive).length}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Aktif Sohbet
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
              color: 'white'
            }}>
              <Group sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {chats.length * 2}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Katılımcı
              </Typography>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ 
              p: 3, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #DEB887 0%, #F4A460 100%)',
              color: 'white'
            }}>
              <Message sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {chats.reduce((total, chat) => total + chat.messageCount, 0)}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Toplam Mesaj
              </Typography>
            </Card>
          </Box>

        </Box>

        {/* Enhanced Filters */}
        <Card elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Chat sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtreler
            </Typography>
          </Box>
          
          <FormControl size="medium" sx={{ minWidth: 200 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              label="Durum"
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">Tüm Sohbetler</MenuItem>
              <MenuItem value="active">Aktif Sohbetler</MenuItem>
            </Select>
          </FormControl>
        </Card>

        {/* Enhanced Chats Table */}
        <Card elevation={0} sx={{ 
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Sohbet ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Katılımcılar</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Mesaj Sayısı</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Son Mesaj Zamanı</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Oluşturulma Tarihi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  chats.map((chat) => (
                    <TableRow 
                      key={chat.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8fafc' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                          #{chat.id.slice(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              src={chat.user1Avatar} 
                              sx={{ 
                                bgcolor: 'primary.main',
                                width: 35,
                                height: 35
                              }}
                            >
                              {!chat.user1Avatar && <Group sx={{ fontSize: 18 }} />}
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {chat.user1Name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                            &
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              src={chat.user2Avatar} 
                              sx={{ 
                                bgcolor: 'secondary.main',
                                width: 35,
                                height: 35
                              }}
                            >
                              {!chat.user2Avatar && <Group sx={{ fontSize: 18 }} />}
                            </Avatar>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {chat.user2Name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={chat.messageCount}
                          size="small"
                          color="info"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {chat.lastMessageTime ? new Date(chat.lastMessageTime).toLocaleString('tr-TR') : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={chat.isActive ? 'Aktif' : 'Pasif'}
                          size="small"
                          color={chat.isActive ? 'success' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(chat.createdAt).toLocaleDateString('tr-TR')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={chats.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{ 
              borderTop: '1px solid #e2e8f0',
              '& .MuiTablePagination-select': { borderRadius: 1 }
            }}
          />
        </Card>
      </Box>
    </Box>
  );
};

export default ChatsPage;
